/**
 * router.options
 * @author wwl
 * @created 2024/10/11
 * @modified 2024/12/19
 */
import type { RouterOptions } from '@nuxt/schema'
import { defineAsyncComponent } from 'vue'
import { getLanguageByHost } from './utils/language-detection'

const enRoutes = [
  {
    name: 'Home',
    path: '/',
    component: defineAsyncComponent(() => import('~/pages/home/Home-en.vue'))
  },
  {
    name: '展会',
    path: '/exhibition/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index-en.vue'))
  },
  {
    name: '国际展会',
    path: '/exhibition-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index-en.vue'))
  },
  {
    name: 'exhibitionDetail',
    path: '/exhibition/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail-en.vue'))
  },
  {
    name: '展会门票2',
    path: '/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket-en.vue'))
  },
  {
    name: '展会资讯',
    path: '/information/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News-en.vue'))
  },
  {
    name: '展会资讯-分页',
    path: '/information-:t-:i-:page/',
    component: defineAsyncComponent(() => import('~/pages/news/list/News-en.vue'))
  },
  {
    name: '资讯详情',
    path: '/information/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details-en.vue'))
  },
  {
    name: '会展中心-',
    path: '/pavilion-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/center/Center-en.vue'))
  },
  {
    name: '会展中心',
    path: '/pavilion/',
    component: defineAsyncComponent(() => import('~/pages/center/Center-en.vue'))
  },
  {
    name: '会展中心详情',
    path: '/pavilion/:id.html',
    component: defineAsyncComponent(() => import('~/pages/center/Details-en.vue'))
  },
  {
    name: '展会合作',
    path: '/cooperation.html',
    component: defineAsyncComponent(() => import('~/pages/cooperation/Cooperation.vue'))
  },
  {
    name: '支付状态',
    path: '/result/:status',
    component: defineAsyncComponent(() => import('~/pages/pay/Result-en.vue'))
  },
  {
    name: 'Login',
    path: '/login',
    component: defineAsyncComponent(() => import('~/pages/login/index.vue'))
  },
  {
    name: 'User Center',
    path: '/expouser/:id.html',
    component: defineAsyncComponent(() => import('~/pages/expouser/index-en.vue'))
  },
  {
    name: 'User Management Center',
    path: '/manager',
    component: defineAsyncComponent(() => import('~/pages/manager/index.vue')),
    meta: {
      isLogin: true
    },
    children: [
      {
        name: 'Change Password',
        path: '/manager/update-pwd',
        component: defineAsyncComponent(() => import('~/pages/manager/update-pwd.vue'))
      },
      {
        name: 'Enterprise Authentication',
        path: '/manager/auth',
        component: defineAsyncComponent(() => import('~/pages/manager/auth.vue'))
      },
      {
        name: 'My Exhibitions',
        path: '/manager/fair-my',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-my.vue'))
      },
      {
        name: 'My Tickets',
        path: '/manager/ticket-my',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-my.vue')),
        meta: {
          isLogin: true
        }
      },
      {
        name: 'Visitor Management',
        path: '/manager/visitor',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor.vue'))
      },
      {
        name: 'Update Info',
        path: '/manager/update-info',
        component: defineAsyncComponent(() => import('~/pages/manager/update-info.vue'))
      },
      {
        name: 'My Visa',
        path: '/manager/visa-my',
        component: defineAsyncComponent(() => import('~/pages/manager/visa.vue'))
      },
      // 新增的manager模块页面
      {
        name: 'Exhibition Reminders',
        path: '/manager/fair-remind',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-remind.vue'))
      },
      {
        name: 'Followed Exhibitions',
        path: '/manager/fair-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-follow.vue'))
      },
      {
        name: 'Followed News',
        path: '/manager/news-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/news-follow.vue'))
      },
      {
        name: 'My News',
        path: '/manager/news-my',
        component: defineAsyncComponent(() => import('~/pages/manager/news-my.vue'))
      },
      {
        name: 'Followed Users',
        path: '/manager/usersFollow',
        component: defineAsyncComponent(() => import('~/pages/manager/users-follow.vue'))
      },
      {
        name: 'My Fans',
        path: '/manager/usersFans',
        component: defineAsyncComponent(() => import('~/pages/manager/users-fans.vue'))
      },
      {
        name: 'Followed Industries',
        path: '/manager/industry-my',
        component: defineAsyncComponent(() => import('~/pages/manager/industry-my.vue'))
      },
      {
        name: 'Visitor Edit',
        path: '/manager/visitor-edit/:id?',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor-edit.vue'))
      },
      {
        name: 'My Periodicals',
        path: '/manager/periodical-my',
        component: defineAsyncComponent(() => import('~/pages/manager/periodical-my.vue'))
      }
    ]
  },
  {
    name: 'Visa Payment Result',
    path: '/visa-result/:status',
    component: defineAsyncComponent(() => import('~/pages/visa/[status].vue'))
  },
  {
    name: 'Special Topic',
    path: '/special/:id.html',
    component: defineAsyncComponent(() => import('~/pages/special/index.vue'))
  },
  {
    name: 'Exhibition Info',
    path: '/expoinfo/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  },
  {
    name: 'Community',
    path: '/community/:id.html',
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  {
    name: 'Exhibitors List',
    path: '/exhibitors/:id.html',
    // component: () => import('~/pages/company/list.vue')
    component: defineAsyncComponent(() => import('~/pages/exhibitors/[id].vue'))
  },
  {
    name: 'Company Detail',
    path: '/company/:id.html',
    component: defineAsyncComponent(() => import('~/pages/company/detail.vue'))
  },
  {
    name: 'Periodical Detail',
    path: '/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  {
    name: 'Exhibition Periodical Detail',
    path: '/exhibition/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical.vue'))
  },
  {
    name: 'Terms of Service',
    path: '/terms',
    component: defineAsyncComponent(() => import('~/pages/terms/index.vue'))
  }
]

const zhRoutes = [
  {
    name: '首页',
    path: '/',
    component: defineAsyncComponent(() => import('~/pages/home/Home.vue'))
  },
  // {
  //   name: '首页seo',
  //   path: '/seo',
  //   component: defineAsyncComponent(() => import('~/pages/home/Home.vue'))
  // },
  {
    name: '展会列表限流',
    path: '/queue/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/queue.vue'))
  },
  {
    name: '展会',
    path: '/exhibition/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index.vue'))
  },
  {
    name: '国际展会',
    path: '/exhibition-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/fair/list/index.vue'))
  },
  // {
  //   name: '展会SEO',
  //   path: '/seo/exhibition/',
  //   component: defineAsyncComponent(() => import('~/pages/fair/list/index-seo.vue'))
  // },
  // {
  //   name: '国际展会SEO',
  //   path: '/seo/exhibition-:f-:s-:g-:p-:c-:t-:page/',
  //   component: defineAsyncComponent(() => import('~/pages/fair/list/index-seo.vue'))
  // },
  // {
  //   name: '国际展会列表SEO',
  //   path: '/seo/exhibition-list/exhibition-:f-:s-:g-:p-:c-:t-:page/',
  //   component: defineAsyncComponent(() => import('~/pages/fair/list/index-seo.vue'))
  // },
  {
    name: '展会资讯',
    path: '/information/',
    // component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
    component: defineAsyncComponent(() => import('~/components/Dynamic/NewsListDynamic.vue'))
  },
  {
    name: '展会资讯-分页',
    path: '/information-:t-:i-:page/',
    // component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
    component: defineAsyncComponent(() => import('~/components/Dynamic/NewsListDynamic.vue'))
  },
  // {
  //   name: '展会资讯seo',
  //   path: '/seo/information/',
  //   component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  // },
  // {
  //   name: '展会资讯-分页seo',
  //   path: '/seo/information-:t-:i-:page/',
  //   component: defineAsyncComponent(() => import('~/pages/news/list/News.vue'))
  // },
  {
    name: '资讯详情',
    path: '/information/:id.html',
    component: defineAsyncComponent(() => import('~/pages/news/details/Details.vue'))
  },
  // {
  //   name: '资讯详情seo',
  //   path: '/seo/information/:id.html',
  //   component: defineAsyncComponent(() => import('~/pages/news/details/Details-seo.vue'))
  // },
  {
    name: '资讯详情2',
    path: '/exponews/:id.html',
    // component: defineAsyncComponent(() => import('~/components/Dynamic/ExpoNewsDynamic.vue'))
    component: defineAsyncComponent(() => import('~/components/Dynamic/NewsDetailDynamic.vue'))
  },
  // {
  //   name: '资讯详情2seo',
  //   path: '/seo/exponews/:id.html',
  //   component: defineAsyncComponent(() => import('~/pages/news/details/exponews-seo.vue'))
  // },
  {
    name: '会展中心-',
    path: '/pavilion-:f-:s-:g-:p-:c-:t-:page/',
    component: defineAsyncComponent(() => import('~/pages/center/Center.vue'))
  },
  {
    name: '会展中心',
    path: '/pavilion/',
    component: defineAsyncComponent(() => import('~/pages/center/Center.vue'))
  },
  {
    name: '会展中心详情',
    path: '/pavilion/:id.html',
    component: defineAsyncComponent(() => import('~/pages/center/Details.vue'))
  },
  {
    name: '展会合作',
    path: '/cooperation.html',
    component: defineAsyncComponent(() => import('~/pages/cooperation/Cooperation.vue'))
  },
  {
    name: 'exhibitionDetail',
    path: '/exhibition/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/detail/detail.vue'))
  },
  // {
  //   name: 'SeoExhibitionDetail',
  //   path: '/seo/exhibition/:id.html',
  //   component: defineAsyncComponent(() => import('~/pages/fair/detail/detail1-seo.vue'))
  // },
  {
    name: '展会门票1',
    path: '/exhibition/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/index.vue'))
  }, // 门票页面旧的路由
  {
    name: '展会门票2',
    path: '/ticket/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/ticket/ticket.vue'))
  },
  {
    name: '支付状态',
    path: '/result/:status',
    component: defineAsyncComponent(() => import('~/pages/pay/Result.vue'))
  },
  // 其他
  {
    name: 'sitemap_index',
    path: '/sitemap_index/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/letter.vue'))
  },
  {
    name: 'sitemap_',
    path: '/sitemap_:letter/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/letterInfo.vue'))
  },
  {
    name: 'sitemap',
    path: '/sitemap/',
    component: defineAsyncComponent(() => import('~/pages/sitemap/index.vue'))
  },
  {
    name: 'health',
    path: '/system/index',
    component: defineAsyncComponent(() => import('~/pages/system/index.vue'))
  },
  {
    name: 'system-error',
    path: '/system/error',
    component: defineAsyncComponent(() => import('~/pages/system/error.vue'))
  },
  {
    name: '关于我们',
    path: '/about/:id.html',
    component: defineAsyncComponent(() => import('~/pages/about/index-nuxt3.vue'))
  },
  {
    name: '用户中心',
    path: '/expouser/:id.html',
    component: defineAsyncComponent(() => import('~/pages/expouser/index.vue'))
  },
  {
    name: '展会百科首页',
    path: '/wiki',
    component: defineAsyncComponent(() => import('~/pages/wiki/index.vue'))
  },
  {
    name: '展会百科列表2',
    path: '/wiki-:s-:page/',
    component: defineAsyncComponent(() => import('~/pages/wiki/list.vue'))
  },
  {
    name: '展会百科详情页',
    path: '/wiki/:id-:a-:h-:aid.html',
    component: defineAsyncComponent(() => import('~/pages/wiki/detail.vue'))
  },
  {
    name: '展团行程',
    path: '/exhibition/trip/:id.html',
    component: defineAsyncComponent(() => import('~/pages/trip/index.vue'))
  },
  {
    name: '展会申请',
    path: '/exhibition/apply/:id.html',
    component: defineAsyncComponent(() => import('~/pages/booth/index.vue'))
    // meta: {
    //   isLogin: true,
    // },
  },
  {
    name: '登录',
    path: '/login',
    component: defineAsyncComponent(() => import('~/pages/login/index.vue'))
  },
  {
    name: '用户管理中心',
    path: '/manager',
    component: defineAsyncComponent(() => import('~/pages/manager/index.vue')),
    meta: {
      isLogin: true
    },
    children: [
      {
        name: '修改密码',
        path: '/manager/update-pwd',
        component: defineAsyncComponent(() => import('~/pages/manager/update-pwd.vue'))
      },
      {
        name: '企业认证',
        path: '/manager/auth',
        component: defineAsyncComponent(() => import('~/pages/manager/auth.vue'))
      },
      {
        name: '我的展会',
        path: '/manager/fair-my',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-my.vue'))
      },
      {
        name: '我的门票',
        path: '/manager/ticket-my',
        component: defineAsyncComponent(() => import('~/pages/manager/ticket-my.vue'))
      },
      {
        name: '访客管理',
        path: '/manager/visitor',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor.vue'))
      },
      {
        name: '修改资料',
        path: '/manager/update-info',
        component: defineAsyncComponent(() => import('~/pages/manager/update-info.vue'))
      },
      {
        name: '我的签证',
        path: '/manager/visa-my',
        component: defineAsyncComponent(() => import('~/pages/manager/visa.vue'))
      },
      // 新增的manager模块页面
      {
        name: '展会提醒',
        path: '/manager/fair-remind',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-remind.vue'))
      },
      {
        name: '关注的展会',
        path: '/manager/fair-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/fair-follow.vue'))
      },
      {
        name: '关注公司',
        path: '/manager/compony-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/compony-follow.vue'))
      },
      {
        name: '关注的资讯',
        path: '/manager/news-follow',
        component: defineAsyncComponent(() => import('~/pages/manager/news-follow.vue'))
      },
      {
        name: '我的资讯',
        path: '/manager/news-my',
        component: defineAsyncComponent(() => import('~/pages/manager/news-my.vue'))
      },
      {
        name: '关注的用户',
        path: '/manager/usersFollow',
        component: defineAsyncComponent(() => import('~/pages/manager/users-follow.vue'))
      },
      {
        name: '我的粉丝',
        path: '/manager/usersFans',
        component: defineAsyncComponent(() => import('~/pages/manager/users-fans.vue'))
      },
      {
        name: '关注的行业',
        path: '/manager/industry-my',
        component: defineAsyncComponent(() => import('~/pages/manager/industry-my.vue'))
      },
      {
        name: '访客编辑',
        path: '/manager/visitor-edit/:id?',
        component: defineAsyncComponent(() => import('~/pages/manager/visitor-edit.vue'))
      },
      {
        name: '我的会刊',
        path: '/manager/periodical-my',
        component: defineAsyncComponent(() => import('~/pages/manager/periodical-my.vue'))
      }
    ]
  },
  {
    name: '专题页面',
    path: '/special/:id.html',
    component: defineAsyncComponent(() => import('~/pages/special/index.vue'))
  },
  {
    name: '展会资讯2',
    path: '/expoinfo/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhDetailInf/index.vue'))
  },
  {
    name: '用户社区',
    path: '/community/:id.html',
    component: defineAsyncComponent(() => import('~/pages/community/index.vue'))
  },
  {
    name: '参展商名录',
    path: '/exhibitors/:id.html',
    component: defineAsyncComponent(() => import('~/pages/exhibitors/[id].vue'))
  },
  {
    name: '公司详情',
    path: '/company/:id.html',
    component: defineAsyncComponent(() => import('~/pages/company/[id].vue'))
  },
  {
    name: '签证列表首页',
    path: '/visa',
    component: defineAsyncComponent(() => import('~/pages/visa/visa-[visaAreaId]-[areaId]-[type]-[bizType]-[page].vue'))
  },
  {
    name: '签证列表',
    path: '/visa-:visaAreaId-:areaId-:type-:bizType-:page/',
    component: defineAsyncComponent(() => import('~/pages/visa/visa-[visaAreaId]-[areaId]-[type]-[bizType]-[page].vue'))
  },
  {
    name: '签证详情',
    path: '/visa/:id.html',
    component: defineAsyncComponent(() => import('~/pages/visa/[id].vue'))
  },
  {
    name: '签证订单提交',
    path: '/visa/submitOrder',
    component: defineAsyncComponent(() => import('~/pages/visa/submitOrder.vue'))
  },
  {
    name: '展商名录详情',
    path: '/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical_new.vue'))
  },
  {
    name: '展会会刊详情',
    path: '/exhibition/periodical/:id.html',
    component: defineAsyncComponent(() => import('~/pages/fair/periodical.vue'))
  },
  {
    name: '服务协议',
    path: '/terms',
    component: defineAsyncComponent(() => import('~/pages/terms/index.vue'))
  },
  {
    name: '选择支付方式',
    path: '/payment',
    component: defineAsyncComponent(() => import('~/pages/visa/payment-new.vue'))
  },
  {
    name: '签证支付结果',
    path: '/visa-result/:status',
    component: defineAsyncComponent(() => import('~/pages/visa/payResult.vue'))
  }
]

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterOptions>{
  routes: _routes => {
    // 在服务端，尝试获取请求的 host 信息
    let host = ''

    if (process.server) {
      try {
        // 尝试通过多种方式获取 host 信息
        const nuxtApp = tryUseNuxtApp()
        // 优先从 header 中获取 host
        if (nuxtApp?.ssrContext?.event?.headers) {
          const headers = nuxtApp.ssrContext.event.headers
          host = headers.get('x-forwarded-host') || headers.get('host') || headers.get('Host') || ''
        } else if (nuxtApp?.ssrContext?.event?.node?.req?.headers?.host) {
          host = nuxtApp.ssrContext.event.node.req.headers.host as string
        }

        // 如果还是获取不到，尝试从环境变量或其他来源获取
        if (!host) {
          // 可以从环境变量中获取默认域名
          host = process.env.HOST || 'www.jufair.com'
        }
      } catch (error) {
        // 如果获取失败，使用默认值
        console.warn('Failed to get host in router.options, using default:', error)
        host = 'www.jufair.com'
      }
    } else {
      // 客户端可以通过 window.location.host 获取
      if (typeof window !== 'undefined') {
        host = window.location.host
      }
    }

    // 根据 host 判断语言
    const language = getLanguageByHost(host)

    // 🔧 英文域名时，如果英文路由找不到，回退到中文路由
    if (language === 'en') {
      // 合并英文路由和中文路由，英文优先，中文作为回退
      // 只把英文不存在的路径导入
      const enPaths = new Set(enRoutes.map(r => r.path))
      const zhOnlyRoutes = zhRoutes.filter(r => !enPaths.has(r.path))
      const mergedRoutes = [...enRoutes, ...zhOnlyRoutes]
      return mergedRoutes
    } else {
      // 中文域名只使用中文路由
      //console.log(`[Router] 🇨🇳 中文域名使用中文路由: ${host}`)
      return zhRoutes
    }
  },
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
}
